// ================================================================================================
import _ from "lodash";
import * as Sentry from "@sentry/react";

import { BodyRawType, RestAPIInfo } from "./RestAPIEditor";
import { Macros } from '../ModelViews/LibraryStructs';
import { Sandbox } from '@Utils/Sandbox/Sandbox';
import * as ModelViews from "../ModelViews";
import { isDefined, isObject, isString, IsNullOrWhiteSpace, isFunction } from '@Utils/Micro';
import { DebugEx, SentryAutoSpan } from '@Utils/DebugEx';
import { ServerPaths, EmeraldBaseUri } from '@Utils/Paths';
import { UserManager } from "@Utils/UserManager";
import { getGlobal } from "@Utils/Globals";
import { BoardMacroEngine } from "@UI/Dashboard/BoardMacroEngine";
import { Span } from "@sentry/react";
// ================================================================================================
export interface MacroLogic {
    /**
     * Macro type
     */
    Type: Macros.MacroScriptType;

    /**
     * ProjectID, this is specify from application.
     * For emerald is the board id. This is helpful for access control.
     */
    ProjectId: string;

    /**
     * Macro ID in the db
     */
    MacroId: string;

    /**
     * Version of the macro, -1 mean the latest
     */
    MacroVersion: number;

    /**
     * Preparation script
     */
    JSPrepScript: string;

    /**
     * SQL Actual Script 
     */
    QueryScript: string;

    /**
     * SQL Connection string
     */
    QueryConnectionString: string;

    /**
     * Handling server response
     */
    JSRespHandlingScript: string;

    /**
     * Input for js prep
     */
    JSPrepInput: any;

    /**
     * Rest settings valid when we are using rest type of logic
     */
    RestSettings?: RestAPIInfo;
}
// ------------------------------------------------------------------------------------------------
export interface MacroLogicJSHandlingInput {
    /**
     * Id of the macro
     */
    MacroId?: string;

    /**
     * Version of the macro, -1 mean the latest
     */
    MacroVersion?: number;

    /**
     * Input of js prep script
     */
    JSPrepInput?: any;

    /**
     * Intermediate results from preparation script.
     */
    JSPrepResults?: any;

    /**
     * Intermediate results from SQL/Influx/Rest script
     */
    QueryResults?: any;

    /**
     * Intermediate results from SQL/Influx/Rest script
     */
    QueryMetadata?: {
        RestRespHeaders?: any;
        RestReqHeaders?: any;
    };

    /**
     * Errors messages from execution
     */
    Error?: {
        from: "JSPrep" | "SQL" | "REST" | "JSRespHandling";
        msg: string;
        name?: string;
        code?: any;
        body?: any;
    }[];
}
export interface MacroLogicResults extends MacroLogicJSHandlingInput {
    /**
     * Flag when the user stopped the execution
     */
    StoppedFromUser?: boolean;

    /**
     * Intermediate results from Data handling script
     */
    JSRespHandlingResults?: any;

    /**
     * Calculate performance times
     */
    Performance?: {
        JSPrep?: number;
        JSPrepExec?: number;
        Query?: number;
        JSHandling?: number;
        JSHandlingExec?: number;
    }
}

// ------------------------------------------------------------------------------------------------
export class MacroLogicEngine {
    public JSRunner: Sandbox;
    constructor() {
        this.JSRunner = new Sandbox("JSMacroRunner", [EmeraldBaseUri + "Content/MacroUtilLib/App.js"]);
    }
    // --------------------------------------------------------------------------------------------
    async RunQueryExecute(
        logic: Partial<MacroLogic>,
        result: MacroLogicResults): Promise<MacroLogicResults> {
        try {
            let queryParameters = result.JSPrepResults;
            if (!_.isArray(queryParameters))
                queryParameters = [queryParameters];
            let req: ModelViews.Macros.ExecuteSQLReq = {
                MacroID: logic.MacroId,
                MacroVersion: logic.MacroVersion,
                ConnectionStr: logic.QueryConnectionString,
                Query: logic.QueryScript,
                QueryParameters: queryParameters,
                ProjectID: logic.ProjectId
            };

            let pathInfo = ServerPaths["GemExecuteSQL"];
            if (logic.Type == Macros.MacroScriptType.Influx)
                pathInfo = ServerPaths["GemExecuteInfluxQL"];
            else if (logic.Type == Macros.MacroScriptType.Flux)
                pathInfo = ServerPaths["GemExecuteInfluxFlux"];

            let fetchSettings: RequestInit = {
                method: pathInfo.Method,
                body: JSON.stringify(req)
            };

            // Put user tokens
            if (process.env.GLOW_BUILD) {
                if (fetchSettings.headers == null)
                    fetchSettings.headers = {};
                let userManager = UserManager.GetInstance();
                let accessToken = userManager.getAccessToken();
                fetchSettings.headers["Authorization"] = "Bearer " + accessToken;
                fetchSettings.headers["Content-Type"] = "application/json";
            }

            // Get results 
            let queryServerResponse = await fetch(pathInfo.Path, fetchSettings);
            try {
                // may error if there is no body
                let dataIn: ModelViews.Macros.ExecuteSQLResp = await queryServerResponse.json();
                //DebugEx.TraceError(dataIn);
                if (dataIn.IsSuccess) {
                    // Deserialize results from sql and execute js handling
                    try {
                        result.QueryResults = null;
                        if (isDefined(dataIn.JsonResults)) {
                            if (dataIn.JsonResults.length == 1)
                                result.QueryResults = JSON.parse(dataIn.JsonResults[0]);
                            else if (dataIn.JsonResults.length > 1)
                                result.QueryResults = _.map(dataIn.JsonResults, x => JSON.parse(x));
                        }
                    } catch (er) {
                        // Failed to deserialized sql results 
                        result.Error.push({
                            from: "SQL",
                            msg: er
                        });
                    }
                } else {
                    // SQL failed in server execution
                    result.Error.push({
                        from: "SQL",
                        msg: dataIn.ErrorStr
                    });
                }
            } catch (ex) {
                result.Error.push({
                    from: "SQL",
                    msg: "Failed to parse server response"
                });
            }
        } catch (ex) {
            result.Error.push({
                from: "SQL",
                msg: ex.message
            });
        }

        return result;
    }
    // --------------------------------------------------------------------------------------------
    async RunRestAPI(
        logic: MacroLogicResults,
        settings: RestAPIInfo
    ) {
        return new Promise<MacroLogicResults>((resolve, reject) => {
            let url = settings.URLPrefix + settings.URL;
            // replace url with parameters
            try {
                const regex = /(\$\{([\w\.\[\]]+)\})/gm;
                let m: RegExpExecArray;
                while ((m = regex.exec(url)) !== null) {
                    // This is necessary to avoid infinite loops with zero-width matches
                    if (m.index === regex.lastIndex) {
                        regex.lastIndex++;
                    }

                    // The result can be accessed through the `m`-variable.
                    if (m.length == 3) {
                        let value = _.get(logic.JSPrepResults, m[2]);
                        // If is object or number then make it to string
                        if (isDefined(value) && !isString(value)) {
                            if (isObject(value))
                                value = JSON.stringify(value)
                            else
                                value = value + "";
                        }
                        if (!IsNullOrWhiteSpace(value))
                            url = url.replace(m[1], value);
                        else
                            url = url.replace(m[1], "");

                        // From beginning the index at which to start the next match.
                        regex.lastIndex = 0;
                    }
                }
            } catch (ex) {
                DebugEx.TraceError(ex);
            }

            // Define parameters 
            if (isDefined(settings.Parameters) &&
                settings.Parameters.length > 0) {
                url += "?";
                _.forEach(settings.Parameters, p => {
                    // Add the key only if we have default value or the js prep  results contain the field
                    if (!IsNullOrWhiteSpace(p.DefaultValue) ||
                        _.has(logic.JSPrepResults, p.Field)) {
                        url += encodeURIComponent(p.Key);
                        let value: string | number | boolean = p.DefaultValue;

                        if (!IsNullOrWhiteSpace(p.Field))
                            value = _.get(logic.JSPrepResults, p.Field);

                        if (_.isObject(value))
                            value = JSON.stringify(value);
                        else if (_.isNumber(value))
                            value = value.toString();
                        else if (!_.isBoolean(value) && IsNullOrWhiteSpace(value))
                            value = "";

                        url += "=" + encodeURIComponent(value) + "&";
                    }
                });

                // Remove last & or ?
                url = url.substring(0, url.length - 1);
            }

            let headers = {};
            _.forEach(settings.Headers, h => {
                let value = h.DefaultValue;
                if (!IsNullOrWhiteSpace(h.Field))
                    value = _.get(logic.JSPrepResults, h.Field);
                if (!IsNullOrWhiteSpace(value))
                    headers[h.Key] = value;
            });

            let body = null;

            // Handle Post raw body 
            if (settings.Type != "GET" &&
                !IsNullOrWhiteSpace(settings.BodyField)) {
                // TODO: fill body for form data
                //let a = new FormData();
                //a.append("test", "testing");

                body = _.get(logic.JSPrepResults, settings.BodyField);
                if (settings.BodyType == "raw" &&
                    settings.ContentType == BodyRawType.JSON) {
                    if (isObject(body))
                        body = JSON.stringify(body);
                    if (IsNullOrWhiteSpace(headers["Content-Type"]))
                        headers["Content-Type"] = "application/json";
                }
            }

            // Handle form data body
            if (settings.Type != "GET" &&
                settings.BodyType == "form-data") {
                body = new FormData();
                _.forEach(settings.BodyFormData, x => {
                    if (!IsNullOrWhiteSpace(x.Field)) {
                        let value = _.get(logic.JSPrepResults, x.Field);
                        (body as FormData).append(x.Key, value);
                    } else if (!IsNullOrWhiteSpace(x.DefaultValue)) {
                        (body as FormData).append(x.Key, x.DefaultValue);
                    }
                });
            }

            if (settings.Type != "GET" &&
                settings.BodyType == "x-www-form-urlencoded") {
                let formData = {};
                _.forEach(settings.BodyFormData, x => {
                    if (!IsNullOrWhiteSpace(x.Field)) {
                        let value = _.get(logic.JSPrepResults, x.Field);
                        formData[x.Key] = value;
                    } else if (!IsNullOrWhiteSpace(x.DefaultValue))
                        formData[x.Key] = x.DefaultValue;
                });

                body = Object.keys(formData)
                    .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(formData[key]))
                    .join('&');
            }

            // Add JWT info
            if (settings.IncludeJWT &&
                IsNullOrWhiteSpace(headers["Authorization"])) {
                if (process.env.GLOW_BUILD) {
                    headers["Authorization"] = "Bearer " + UserManager.GetInstance()?.getAccessToken();
                } else {
                    headers["Authorization"] = "Bearer " + BoardMacroEngine.GetAccessToken();
                }
            }

            // Add custom headers for debugging like 
            // TODO: make option for the headers. CORS problem in the case that the server is not expecting this headers
            /*
            headers["y-gem-id"] = logic.MacroId;
            headers["y-gem-ver"] = logic.MacroVersion;
            */

            // Execute rest api
            fetch(url,
                {
                    method: settings.Type,
                    mode: "cors",
                    body: body,
                    headers: headers,
                    credentials: (settings.IncludeCredentials) ? "include" : "omit",
                })
                .then(res => {
                    //console.error(res);

                    // Extract headers response
                    let respHeaders = {};
                    for (var pair of (res.headers as any)) {
                        respHeaders[pair[0]] = pair[1];
                    }

                    // Check the response
                    if (res.ok) {
                        let respCB = (resp) => {
                            return {
                                headers: respHeaders,
                                results: resp
                            };
                        };
                        if (res.headers.has("Content-Type") &&
                            res.headers.get("Content-Type").indexOf("application/json") > -1)
                            return res.json().then(respCB);
                        else
                            return res.text().then(respCB);
                    } else {
                        // Handling error by extract body data
                        if (isFunction(res.json) &&
                            res.headers.has("Content-Type") &&
                            res.headers.get("Content-Type").indexOf("application/json") > -1)
                            return res.json()
                                .then(function (err) {
                                    throw new RestAPIError(res.statusText, res.status, err);
                                });
                        else if (isFunction(res.text))
                            return res.text()
                                .then(function (err) {
                                    throw new RestAPIError(res.statusText, res.status, err);
                                });
                        else
                            throw new RestAPIError(res.statusText, res.status, null);
                    }
                })
                .then((resp) => {
                    //console.error(resp);
                    logic.QueryResults = resp.results;
                    logic.QueryMetadata = {
                        RestReqHeaders: headers,
                        RestRespHeaders: resp.headers
                    };
                    resolve(logic);
                })
                .catch((ex: Error) => {
                    logic.QueryResults = null;
                    logic.QueryMetadata = undefined;
                    if (!isDefined(logic.Error))
                        logic.Error = [];

                    if (ex.name == "RestAPIError") {
                        let exRest = ex as RestAPIError;
                        logic.Error.push({
                            from: "REST",
                            msg: ex.message,
                            name: ex.name,
                            code: exRest.httpCode,
                            body: exRest.errorBodyResp
                        });
                    } else {
                        logic.Error.push({
                            from: "REST",
                            msg: ex.message,
                            name: ex.name
                        });
                    }
                    reject(logic);
                });
        });
    }
    // --------------------------------------------------------------------------------------------
    async RunMacroStepsSub(
        logic: MacroLogic,
        onStateStart: (state: "JSPrep" | "Query" | "JSHandling") => void = _.noop
    ): Promise<MacroLogicResults> {
        const showPerformanceMarking: boolean = getGlobal("GemProfileMarking");
        if (showPerformanceMarking)
            performance.mark(`[${logic.MacroId}]-Gem-Start`);
        let t0 = performance.now();

        try {
            let result: MacroLogicResults = {
                MacroId: logic.MacroId,
                MacroVersion: logic.MacroVersion,
                JSPrepInput: logic.JSPrepInput,
                Performance: {
                    JSPrep: 0,
                    JSPrepExec: 0,
                    JSHandling: 0,
                    JSHandlingExec: 0,
                    Query: 0
                },
                Error: []
            };

            // Run JS prep code
            await Sentry.startSpan({
                name: 'JSPrep',
                op: 'func',
            }, async (spanJSPrep) => {
                try {
                    onStateStart("JSPrep");
                    let jsPrepResults = await this.JSRunner.EvalAsync(logic.JSPrepScript, logic.JSPrepInput, 'input', `${logic.MacroId} - JSPrep`);
                    result.JSPrepResults = jsPrepResults.results;
                    result.Performance.JSPrepExec = jsPrepResults.time;
                    spanJSPrep?.setStatus({ code: 1 /* OK */, message: "ok" });
                } catch (ex) {
                    spanJSPrep?.setStatus({ code: 2 /* Error */, message: ex.error });
                    DebugEx.TraceError(ex);
                    return {
                        MacroId: logic.MacroId,
                        MacroVersion: logic.MacroVersion,
                        JSPrepInput: logic.JSPrepInput,
                        Performance: {
                            JSPrep: performance.now() - t0,
                            JSPrepExec: ex.time
                        },
                        Error: [{
                            from: "JSPrep",
                            msg: ex.error,
                        }]
                    };
                } finally {
                    spanJSPrep?.end();
                }
            });

            result.Performance.JSPrep = performance.now() - t0;
            if (showPerformanceMarking)
                performance.mark(`[${logic.MacroId}]-Gem-JSPrep-End`);
            t0 = performance.now();

            // The result is undefine stop the execution here
            if (result.JSPrepResults == null)
                return result;

            // If we are running Javascript execution then return here
            if (logic.Type == Macros.MacroScriptType.Javascript)
                return result;

            // ====================================================================================== +++ QUERY
            onStateStart("Query");
            if (logic.Type == Macros.MacroScriptType.RestAPI) {
                //------------------------------------------------------  Rest
                await Sentry.startSpan({
                    name: 'RestAPI',
                    op: 'func',
                    attributes: {
                        url: logic.RestSettings?.URL
                    }
                }, async (spanQuery) => {
                    try {
                        result = await this.RunRestAPI(result, logic.RestSettings);
                        spanQuery?.setStatus({ code: 1 /* OK */, message: "ok" });
                    } catch (ex) {
                        // The error is part of the fetch
                        if (ex == result) {
                            result = ex;
                        } else {
                            DebugEx.TraceError(ex);
                        }

                        spanQuery?.setStatus({ code: 2 /* Error */, message: ex.error });
                    }
                    spanQuery?.end();
                });

            } else {
                //------------------------------------------------------  SQL/InfluxQL/InfluxFlux

                await Sentry.startSpan({
                    name: 'Query',
                    op: 'func'
                }, async (spanQuery) => {
                    result = await this.RunQueryExecute(logic, result);
                    if (_.isEmpty(result.Error)) {
                        spanQuery?.setStatus({ code: 1 /* OK */, message: "ok" });
                    } else {
                        spanQuery?.setStatus({ code: 2 /* Error */, message: result.Error[0].msg });
                    }
                    spanQuery?.end();
                });
            }

            result.Performance.Query = performance.now() - t0;
            if (showPerformanceMarking)
                performance.mark(`[${logic.MacroId}]-Gem-Query-End`);
            t0 = performance.now();

            // ====================================================================================== --- QUERY

            // Show null in error array if is empty
            if (isDefined(result.Error) &&
                result.Error.length == 0)
                result.Error = null;


            // Run JS handling code
            await Sentry.startSpan({
                name: 'JSHandling',
                op: 'func',
                attributes: {
                    url: logic.RestSettings?.URL
                }
            }, async (spanJSHandling) => {
                try {
                    onStateStart("JSHandling");
                    let jsHandlingResults = await this.JSRunner.EvalAsync(logic.JSRespHandlingScript, result, 'input', `${logic.MacroId} - JSHandling`);
                    result.JSRespHandlingResults = jsHandlingResults.results;
                    result.Performance.JSHandlingExec = jsHandlingResults.time;
                    spanJSHandling?.setStatus({ code: 1 /* OK */, message: "ok" });
                } catch (error) {
                    // JS handling error
                    if (result.Error == null)
                        result.Error = [];
                    result.Performance.JSHandlingExec = error.time;
                    result.Error.push({
                        from: "JSRespHandling",
                        msg: error.error
                    });
                    spanJSHandling?.setStatus({ code: 2 /* Error */, message: error.error });
                }

                spanJSHandling?.end();
            });

            result.Performance.JSHandling = performance.now() - t0;
            t0 = performance.now();

            if (showPerformanceMarking) {
                performance.mark(`[${logic.MacroId}]-Gem-JSHandling-End`);
                performance.measure(`[${logic.MacroId}]-Gem`, `[${logic.MacroId}]-Gem-Start`, `[${logic.MacroId}]-Gem-JSHandling-End`);
                performance.measure(`[${logic.MacroId}]-Gem-JSPerp`, `[${logic.MacroId}]-Gem-Start`, `[${logic.MacroId}]-Gem-JSPrep-End`);
                performance.measure(`[${logic.MacroId}]-Gem-Query`, `[${logic.MacroId}]-Gem-JSPrep-End`, `[${logic.MacroId}]-Gem-Query-End`);
                performance.measure(`[${logic.MacroId}]-Gem-JSHandling`, `[${logic.MacroId}]-Gem-Query-End`, `[${logic.MacroId}]-Gem-JSHandling-End`);
            }
            // Return the response
            return result;
        } catch (ex) {
            DebugEx.TraceError(ex);


            if (showPerformanceMarking) {
                performance.mark(`[${logic.MacroId}]-Gem-Error-End`);
                performance.measure(`[${logic.MacroId}]-Gem-Error`, `[${logic.MacroId}]-Gem-Start`, `[${logic.MacroId}]-Gem-Error-End`);
            }

            // JS preparation errors
            let result: MacroLogicResults = {
                MacroId: logic.MacroId,
                MacroVersion: logic.MacroVersion,
                JSPrepInput: logic.JSPrepInput,
                Performance: {
                    JSPrep: performance.now() - t0,
                    JSPrepExec: ex.time
                },
                Error: [{
                    from: "JSPrep",
                    msg: ex.error
                }]
            };
            return result;
        }
    }
    // --------------------------------------------------------------------------------------------
    async RunMacroSteps(logic: MacroLogic,
        onStateStart: (state: "JSPrep" | "Query" | "JSHandling") => void = _.noop
    ): Promise<MacroLogicResults> {
        return await SentryAutoSpan(
            logic.MacroId,
            this.RunMacroStepsSub.bind(this),
            {
                Type: logic.Type,
                MacroId: logic.MacroId,
                Version: logic.MacroVersion,
                ProjectId: logic.ProjectId
            })(logic, onStateStart);
    }


    // --------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------
class RestAPIError extends Error {
    public errorBodyResp;
    public httpCode;
    constructor(message, httpCode, errorBodyResp) {
        super(message);
        this.name = "RestAPIError";
        this.httpCode = httpCode;
        this.errorBodyResp = errorBodyResp;
    }
}